import { useState } from 'react';

import { getAvatars } from '@innovamat/glimmer-assets';

import {
  ImageSelector,
  Input,
  Modal,
  Select,
  Typography,
} from '@innovamat/glimmer-components';
import type { ClassroomBody, GlowApiError } from '@innovamat/glow-api-client';

import styled from '@emotion/styled';
import { getRandomItem, newId, stages } from '@innovamat/radiance-utils';
import { useTranslation } from 'react-i18next';
import { normalizeImgSource } from '../../../../utils';
import { useStage } from '../../../shared';
import { useClassroomCapabilities } from '../../hooks/use-classroom-capabilities';
import { useOrganization } from '../../hooks/use-organization';
import { usePutClassroom } from '../../hooks/use-put-classroom';
import { useSchoolCapabilities } from '../../hooks/use-school-capabilities';
import type { Classroom, ClassroomType } from '../../modules/classroom';
import {
  CLASSROOM_TYPE,
  DEFAULT_COURSE_ORDER_FOR_GROUPS,
} from '../../modules/classroom';
import { AddStudentsToClassroomModal } from '../add-students-to-classroom-modal';
import { ClassroomSchedule } from '../classroom-schedule';
import { DigitalPracticeSection } from '../digital-practice-section/digital-practice-section';
import type { TempClassroom } from './hooks/use-classroom-modal-state';
import { useClassroomModalState } from './hooks/use-classroom-modal-state';
import { useClassroomValidation } from './hooks/use-classroom-validation';
import { useStudentsEnrollment } from './hooks/use-students-enrollment';

const Section = styled.div`
  display: flex;
  flex-direction: column;
`;

const Content = styled.div`
  display: flex;
  gap: 16px;
  margin-top: 8px;
  width: 100%;
  > div {
    flex: 1;
  }
`;

const ModalContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 32px;
`;

const AliasInputSection = styled.div`
  margin-top: 16px;
  width: calc(50% - 8px);
`;

type Props = {
  classroom?: Classroom | undefined;
  onClose: () => void;
  show: boolean;
  availableCoursesIds?: string[];
  onSuccess?: (classroomBody: ClassroomBody) => void;
  onError?: (errors: GlowApiError) => void;
  isCourseSelectorAutofilled?: boolean;
  classroomType?: ClassroomType;
  zIndex?: number;
};

function getImages(courseOrder: number | undefined) {
  if (!courseOrder) return [];

  return Object.entries(getAvatars(courseOrder)).map(([key, value]) => ({
    id: +key,
    image: normalizeImgSource(value),
  }));
}

function AddClassroomModal({
  availableCoursesIds,
  classroom,
  classroomType,
  isCourseSelectorAutofilled,
  show,
  onClose,
  onError,
  onSuccess,
  zIndex,
}: Props): JSX.Element | null {
  const { t } = useTranslation();
  const { organization } = useOrganization();
  const type = classroomType || (classroom?.type as ClassroomType);

  const {
    classrooms: { canEdit },
  } = useSchoolCapabilities();

  const {
    classroom: { canEditAlias },
  } = useClassroomCapabilities({ classroom: classroom });

  const { handlePutClassroom, isPending } = usePutClassroom({
    onError,
    onSuccess: (classroomBody: ClassroomBody) => {
      if (type === CLASSROOM_TYPE.JOURNEYS && !isEditing) {
        setTempClassroomBody(classroomBody);
        setCurrentStep('students');
      } else {
        if (onSuccess) {
          onSuccess(classroomBody);
        }
      }
    },
  });

  const [tempClassroomBody, setTempClassroomBody] =
    useState<ClassroomBody | null>(null);

  const { handleAddStudents, isLoadingStudentsMutation, failures } =
    useStudentsEnrollment({
      onSuccess: (classroomBody) => {
        onSuccess?.(classroomBody);
      },
    });

  const {
    coursesOptions,
    currentStep,
    schedule,
    selectedCourse,
    state,
    setCurrentStep,
    setState,
  } = useClassroomModalState({
    classroom,
    show,
    availableCoursesIds,
  });

  const currentCourseOrder =
    type === CLASSROOM_TYPE.JOURNEYS
      ? DEFAULT_COURSE_ORDER_FOR_GROUPS
      : selectedCourse?.order;
  const { isClassroomManagerExperience } = useStage({
    courseOrder: currentCourseOrder,
  });

  const isEditing = !!classroom?.id;
  const isSecondarySelected = !!stages.isSecundaria(currentCourseOrder);

  const { isValid } = useClassroomValidation({
    state,
    isSecondarySelected,
    classroomType: type,
  });

  const avatarImages = currentCourseOrder ? getImages(currentCourseOrder) : [];

  const handleCreateOrNext = (): void => {
    const body: ClassroomBody = {
      id: classroom?.id || newId(),
      icon: state.icon || getRandomItem(avatarImages).id,
      organizationId: organization?.id || '',
      level: state.level,
      name: state.name,
      courseId: type === CLASSROOM_TYPE.JOURNEYS ? undefined : state.courseId,
      avatar: state.avatar,
      scheduleEnd: schedule.values.scheduleEnd,
      scheduleStart: schedule.values.scheduleStart,
      ...(isClassroomManagerExperience && {
        digitalPracticeFirstDayOfWeek: state.digitalPracticeFirstDayOfWeek,
      }),
      type: type,
      alias: state.alias,
    };

    handlePutClassroom(body, schedule.values.timeZone!);
  };

  const handleCloseModal = (): void => {
    setCurrentStep('configuration');
    setTempClassroomBody(null);
    onClose();
  };

  const handleCloseStudentsModal = (): void => {
    if (tempClassroomBody) {
      onSuccess?.(tempClassroomBody);
    }
    handleCloseModal();
  };

  const handleInputChange = (
    value: string | number | null,
    inputName: keyof TempClassroom
  ): void => {
    setState((prevState) => ({ ...prevState, [inputName]: value }));
  };

  const isAllDataValid = schedule.areAllFieldsFilled && isValid;

  const getStatus = () => {
    if (type === CLASSROOM_TYPE.JOURNEYS) {
      return undefined;
    }
    if (!canEdit) {
      return 'disabled';
    }
    if (isCourseSelectorAutofilled) {
      return 'autofilled';
    }
    return undefined;
  };

  if (!show) return null;

  const getTitle = () => {
    if (type === CLASSROOM_TYPE.JOURNEYS) {
      return isEditing
        ? t('classroom.title.editGroup')
        : t('classroom.title.createGroup');
    }

    return isEditing
      ? t('classroom.title.editClassroom')
      : t('classroom.title.createClassroom');
  };

  const getMainButton = () => {
    if (type === CLASSROOM_TYPE.JOURNEYS && !isEditing) {
      return {
        children: t('common.next'),
        dataTestId: 'add-classroom-modal-save-button',
        onClick: isPending ? undefined : handleCreateOrNext,
        variant: 'accent' as const,
        disabled: !isAllDataValid,
        loading: isPending,
      };
    }

    return {
      children: isEditing ? t('common.save') : t('common.create'),
      dataTestId: 'add-classroom-modal-save-button',
      onClick: isPending ? undefined : handleCreateOrNext,
      variant: 'accent' as const,
      disabled: !isAllDataValid,
      loading: isPending,
    };
  };

  if (
    currentStep === 'students' &&
    type === CLASSROOM_TYPE.JOURNEYS &&
    tempClassroomBody
  ) {
    return (
      <AddStudentsToClassroomModal
        show={show}
        onClose={handleCloseStudentsModal}
        isLoading={isLoadingStudentsMutation}
        failures={failures}
        onSave={(emails) =>
          handleAddStudents({
            emails,
            classroomBody: tempClassroomBody,
          })
        }
      />
    );
  }

  return (
    <Modal
      modalWidth={700}
      buttons={[
        getMainButton(),
        {
          dataTestId: 'add-classroom-modal-close-button',
          children: t('common.close'),
          onClick: handleCloseModal,
          variant: 'tertiary' as const,
        },
      ]}
      isOpen={show}
      onClose={handleCloseModal}
      title={getTitle()}
      zIndex={zIndex}
    >
      <ModalContent>
        <Section>
          {type === CLASSROOM_TYPE.CLASSROOM && (
            <Typography.Subtitle2>
              {t('classroom.inputs.name-and-course', 'Nombre y curso')}
            </Typography.Subtitle2>
          )}
          <Content>
            <Input
              dataTestId="add-classroom-modal-name-input"
              ref={(input) => input?.focus()}
              labelText={
                type === CLASSROOM_TYPE.CLASSROOM
                  ? t(`classroom.input.label.classname`)
                  : t(`classroom.input.label.groupname`)
              }
              type="text"
              onChange={(e) => handleInputChange(e.target.value, 'name')}
              placeholder={t('classroom.input.placeholder.writeName')}
              value={state.name}
              status={getStatus()}
            />

            {type === CLASSROOM_TYPE.CLASSROOM && (
              <Select
                dataTestId="add-classroom-modal-course-select"
                menuPortalTarget={document.body}
                labelText={t('classroom.input.placeholder.course')}
                onChange={(option) =>
                  option?.value && handleInputChange(option?.value, 'courseId')
                }
                status={getStatus()}
                options={coursesOptions}
                placeholder={t('classroom.input.label.chooseCourse')}
                value={state.courseId}
              />
            )}
          </Content>
          {canEditAlias && (
            <AliasInputSection>
              <Input
                labelText={t('classroom.input.label.alias')}
                onChange={(e) => handleInputChange(e.target.value, 'alias')}
                placeholder={t('classroom.input.placeholder.alias')}
                value={state.alias}
              />
            </AliasInputSection>
          )}
        </Section>

        <ClassroomSchedule {...schedule} />

        {isClassroomManagerExperience && (
          <Section>
            <DigitalPracticeSection
              key={state.digitalPracticeFirstDayOfWeek}
              classroom={classroom}
              isEditing={isEditing}
              digitalPracticeFirstDayOfWeek={
                state.digitalPracticeFirstDayOfWeek
              }
              digitalPracticeFirstHourOfWeek={
                classroom?.digitalPracticeFirstHourOfWeek || '00:00:00'
              }
              onChange={(value) =>
                handleInputChange(value, 'digitalPracticeFirstDayOfWeek')
              }
            />
          </Section>
        )}

        <Section>
          <Typography.Subtitle2>
            {t('classroom.inputs.avatar')}
          </Typography.Subtitle2>
          <ImageSelector
            dataTestId="add-classroom-modal-image-selector"
            images={avatarImages}
            noImageText={t('classroom.selectImage.defaultText')}
            onSelectAvatar={(avatar) => handleInputChange(avatar, 'avatar')}
            onSelectIcon={(id) => handleInputChange(id, 'icon')}
            selectedAvatar={state.avatar}
            selectedCourseOrder={currentCourseOrder}
            selectedIcon={state.icon}
            t={t}
          />
        </Section>
      </ModalContent>
    </Modal>
  );
}

export { AddClassroomModal };
export type { TempClassroom };
